import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, queryKeys } from '@barber/ui';
import type {
  Appointment,
  Availability,
  BenefitPreview,
  CheckoutResult,
  CustomerProfile,
  MySubscription,
  Paginated,
  PixCharge,
  PlanBalance,
  PublicBarber,
  PublicPlan,
  PublicService,
  Recurrence,
} from '@barber/ui';

/**
 * As consultas e ações do app, uma por endpoint.
 *
 * Ficam juntas de propósito: são o mapa completo do que este app pede à API.
 * Espalhá-las pelas telas esconderia esse mapa, e a primeira consequência
 * prática seria a mesma consulta escrita de duas formas em duas telas.
 */

// ============================================================== catálogo ====

export function useServices() {
  return useQuery({
    queryKey: queryKeys.services,
    queryFn: () => api.get<PublicService[]>('/public/services'),
    staleTime: 5 * 60_000,
  });
}

export function useBarbers(serviceId?: string) {
  return useQuery({
    queryKey: queryKeys.barbers(serviceId),
    queryFn: () =>
      api.get<PublicBarber[]>('/public/barbers', serviceId ? { query: { serviceId } } : {}),
    staleTime: 5 * 60_000,
  });
}

export function usePlans() {
  return useQuery({
    queryKey: queryKeys.plans,
    queryFn: () => api.get<PublicPlan[]>('/public/plans'),
    staleTime: 5 * 60_000,
  });
}

/**
 * Horários livres de um serviço numa data.
 *
 * `staleTime: 0` aqui, contra os 30 s do padrão: entre carregar a tela e tocar
 * num horário, outra pessoa pode ter agendado. Preferimos pedir de novo a
 * oferecer um horário que já era.
 */
export function useAvailability(input: {
  /** Um serviço, ou vários em sequência (na ordem). */
  serviceIds: readonly string[];
  date: string | null;
  barberId?: string | null;
}) {
  const { serviceIds, date, barberId } = input;

  return useQuery({
    queryKey: [...queryKeys.availability(serviceIds.join('+'), date ?? '', barberId ?? undefined)],
    queryFn: () =>
      api.get<Availability>('/public/availability', {
        query: {
          ...(serviceIds.length > 1 ? { serviceIds } : { serviceId: serviceIds[0] }),
          date: date!,
          barberId: barberId ?? undefined,
        },
      }),
    enabled: Boolean(serviceIds.length > 0 && date),
    staleTime: 0,
  });
}

// =========================================================== agendamentos ====

export type AppointmentFilter = 'proximos' | 'historico';

/**
 * `proximos` e `historico` são a MESMA lista, cortada por status.
 *
 * O corte é por status e não por data: um horário de ontem que a barbearia
 * esqueceu de concluir continua PENDING, e some da tela de "meus horários" se o
 * corte for temporal — junto com o botão de cancelar que o cliente ainda pode
 * querer usar.
 */
export function useAppointments(filtro: AppointmentFilter) {
  const statuses =
    filtro === 'proximos'
      ? (['PENDING', 'CONFIRMED'] as const)
      : (['COMPLETED', 'CANCELLED', 'NO_SHOW'] as const);

  return useQuery({
    queryKey: queryKeys.appointments(filtro),
    queryFn: async () => {
      const paginas = await Promise.all(
        statuses.map((status) =>
          api.get<Paginated<Appointment>>('/appointments', {
            query: { status, pageSize: 50 },
          }),
        ),
      );

      const todos = paginas.flatMap((pagina) => pagina.data);

      return todos.sort((a, b) =>
        filtro === 'proximos'
          ? new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
          : new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime(),
      );
    },
  });
}

export function useAppointment(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.appointment(id ?? ''),
    queryFn: () => api.get<Appointment>(`/appointments/${id}`),
    enabled: Boolean(id),
  });
}

/** Invalida tudo que uma mudança de agendamento afeta. */
function useAgendaInvalidation() {
  const client = useQueryClient();

  return () => {
    void client.invalidateQueries({ queryKey: ['appointments'] });
    void client.invalidateQueries({ queryKey: ['appointment'] });
    void client.invalidateQueries({ queryKey: ['availability'] });
    // A cota do plano muda junto: agendar reserva, cancelar devolve.
    void client.invalidateQueries({ queryKey: ['me'] });
  };
}

export function useCreateAppointment() {
  const invalidar = useAgendaInvalidation();

  return useMutation({
    mutationFn: (
      input: {
        barberId: string;
        startsAt: string;
        addOnServiceIds?: string[];
      } & ({ serviceId: string } | { serviceIds: string[] }),
    ) =>
      // Um serviço devolve o agendamento; vários devolvem a sequência.
      api.post<Appointment & { groupId?: string; appointments?: Appointment[] }>(
        '/appointments',
        input,
      ),
    onSuccess: invalidar,
  });
}

/** Acrescenta ou tira um encaixe; a resposta é o horário atualizado. */
export function useAddOnMutation() {
  const invalidar = useAgendaInvalidation();

  return useMutation({
    mutationFn: (input: { appointmentId: string; serviceId: string; acao: 'add' | 'remove' }) =>
      input.acao === 'add'
        ? api.post<Appointment>(`/appointments/${input.appointmentId}/add-ons`, {
            serviceId: input.serviceId,
          })
        : api.delete<Appointment>(
            `/appointments/${input.appointmentId}/add-ons/${input.serviceId}`,
          ),
    onSuccess: invalidar,
  });
}

export function useCancelAppointment() {
  const invalidar = useAgendaInvalidation();

  return useMutation({
    mutationFn: (input: { id: string; reason?: string }) =>
      api.post<Appointment>(`/appointments/${input.id}/cancel`, { reason: input.reason }),
    onSuccess: invalidar,
  });
}

export function useRescheduleAppointment() {
  const invalidar = useAgendaInvalidation();

  return useMutation({
    mutationFn: (input: { id: string; startsAt: string; barberId?: string }) =>
      api.post<Appointment>(`/appointments/${input.id}/reschedule`, {
        startsAt: input.startsAt,
        barberId: input.barberId,
      }),
    onSuccess: invalidar,
  });
}

// ========================================================= horário fixo ====

export function useRecurrences() {
  return useQuery({
    queryKey: ['recurrences'],
    queryFn: () => api.get<Recurrence[]>('/recurrences'),
  });
}

export function useCreateRecurrence() {
  const invalidar = useAgendaInvalidation();
  const client = useQueryClient();

  return useMutation({
    mutationFn: (
      input: { barberId: string; startsAt: string; addOnServiceIds?: string[] } & (
        { serviceId: string } | { serviceIds: string[] }
      ),
    ) => api.post<Recurrence>('/recurrences', input),
    onSuccess: () => {
      invalidar();
      void client.invalidateQueries({ queryKey: ['recurrences'] });
    },
  });
}

export function useCancelRecurrence() {
  const invalidar = useAgendaInvalidation();
  const client = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.post<Recurrence>(`/recurrences/${id}/cancel`, {}),
    onSuccess: () => {
      invalidar();
      void client.invalidateQueries({ queryKey: ['recurrences'] });
    },
  });
}

// ================================================================= plano ====

export function useMySubscription() {
  return useQuery({
    queryKey: queryKeys.mySubscription,
    queryFn: () => api.get<MySubscription>('/me/subscription'),
  });
}

export function usePlanBalance() {
  return useQuery({
    queryKey: queryKeys.myPlanBalance,
    queryFn: () => api.get<PlanBalance>('/me/plan'),
  });
}

/**
 * Quanto ESTE serviço vai custar para ESTE cliente.
 *
 * Consultado na tela de agendamento, antes de confirmar — é o que responde
 * "isso está incluso no meu plano?" sem o cliente precisar arriscar.
 */
export function useBenefitPreview(serviceId: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.benefitPreview(serviceId ?? ''),
    queryFn: () =>
      api.get<BenefitPreview>('/me/plan/preview', { query: { serviceId: serviceId! } }),
    enabled: Boolean(serviceId) && enabled,
    // Sem plano, o servidor responde com o preço cheio. Não é erro, e insistir
    // não muda a resposta.
    retry: false,
  });
}

export function useSubscribeToPlan() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (planId: string) => {
      const criada = await api.post<{ id: string }>('/me/subscription', { planId });

      // O link de pagamento é criado num segundo passo: a assinatura nasce
      // PENDING e só o checkout do Mercado Pago a torna pagável.
      return api.post<CheckoutResult>('/mercadopago/checkout', {
        subscriptionId: criada.id,
      });
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useCancelSubscription() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: { id: string; reason?: string }) =>
      api.post(`/subscriptions/${input.id}/cancel`, { reason: input.reason }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

// ================================================================ perfil ====

export function useProfile() {
  return useQuery({
    queryKey: queryKeys.profile,
    queryFn: () => api.get<CustomerProfile>('/me/profile'),
  });
}

export function useUpdateProfile() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      name?: string;
      email?: string | null;
      birthDate?: string | null;
      /** Só dígitos. O servidor guarda cifrado e devolve mascarado. */
      cpf?: string;
    }) => api.patch<CustomerProfile>('/me/profile', input),
    onSuccess: (perfil) => {
      client.setQueryData(queryKeys.profile, perfil);
    },
  });
}

// ================================================================== PIX ====

/** Gera (ou devolve, se ainda vale) o PIX com desconto de um horário. */
export function usePayAppointmentPix() {
  return useMutation({
    mutationFn: (appointmentId: string) =>
      api.post<PixCharge>(`/me/appointments/${appointmentId}/pix`),
  });
}

/** Contrata o plano pagando por PIX: cria a assinatura e já gera o PIX dela. */
export function useSubscribeByPix() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (planId: string) => {
      await api.post<{ id: string }>('/me/subscription', { planId, paymentMethod: 'PIX' });
      return api.post<PixCharge | { paidWithCredit: true }>('/me/subscription/pix');
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/** Gera o PIX da mensalidade do plano. Crédito que cobre tudo paga sem QR. */
export function usePaySubscriptionPix() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<PixCharge | { paidWithCredit: true }>('/me/subscription/pix'),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/**
 * Acompanha o pagamento: consulta a cada 3 s enquanto está pendente. A
 * confirmação vem do banco (webhook), não da tela.
 */
export function usePixChargeStatus(chargeId: string) {
  return useQuery({
    queryKey: queryKeys.pixCharge(chargeId),
    queryFn: () =>
      api.get<{ status: PixCharge['status']; amountCents: number }>(`/me/pix-charges/${chargeId}`),
    refetchInterval: (query) => (query.state.data?.status === 'PENDING' ? 3_000 : false),
  });
}
